import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { Modal, Field } from "./Fields";
import { api, fmtUSD, fmtNum, todayISO, API } from "../lib/api";
import { Plus, FileText, Trash2, Send, DollarSign, Printer, Check, Mail } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  draft: "bg-[#E5E2D6] text-[#66645E]",
  sent: "bg-[#1F1E1B] text-white",
  partial: "bg-[#B87A00] text-white",
  paid: "bg-[#1A4135] text-white",
  void: "bg-[#BC4727] text-white",
};

export default function PartnerInvoices() {
  const [params, setParams] = useSearchParams();
  const partnerParam = params.get("partner_id") || "";
  const [invoices, setInvoices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [genOpen, setGenOpen] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [emailingId, setEmailingId] = useState(null);
  const [emailConfig, setEmailConfig] = useState({ configured: false });

  const load = async () => {
    const [i, p, ec] = await Promise.all([
      api.get("/partner-invoices"),
      api.get("/partners"),
      api.get("/email/config").catch(() => ({ data: { configured: false } })),
    ]);
    setInvoices(i.data);
    setPartners(p.data);
    setEmailConfig(ec.data);
  };

  useEffect(() => { load(); }, []);

  // If /partner-invoices?partner_id=X is opened, auto-open generator
  useEffect(() => {
    if (partnerParam && partners.length && !genOpen) {
      setGenOpen(true);
    }
  }, [partnerParam, partners.length]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this partner invoice? Any linked expenses will become billable again and reimbursements will be reversed.")) return;
    await api.delete(`/partner-invoices/${id}`);
    toast.success("Invoice deleted");
    load();
  };

  return (
    <div>
      <TopBar
        title="Partner Invoices"
        subtitle="Bill your cost-share collaborators for their share of field costs — itemized by category and per-acre."
        actions={
          <button
            data-testid="new-partner-invoice-btn"
            onClick={() => { setGenOpen(true); }}
            className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#9D391E] transition-colors flex items-center gap-2 mt-6"
          >
            <Plus className="w-4 h-4" /> Generate Invoice
          </button>
        }
      />

      {invoices.length === 0 ? (
        <EmptyState icon={FileText} title="No partner invoices yet" hint="Click Generate Invoice to bill Uncle for his share of a field's costs." testid="empty-partner-invoices" />
      ) : (
        <div className="bg-white border border-[#1F1E1B] overflow-x-auto">
          <table className="ledger-table" data-testid="partner-invoices-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Issued</th>
                <th>Partner</th>
                <th>Fields</th>
                <th className="num">Acres</th>
                <th className="num">Gross</th>
                <th className="num">Share Due</th>
                <th className="num">Paid</th>
                <th>Status</th>
                <th style={{ width: 220 }}></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id} data-testid={`partner-invoice-row-${i.id}`}>
                  <td className="tabular font-semibold">{i.invoice_number}</td>
                  <td className="tabular">{i.issue_date}</td>
                  <td>{i.partner_name}</td>
                  <td className="text-xs">{(i.field_names || []).join(", ") || "—"}</td>
                  <td className="num">{fmtNum(i.total_acres, 1)}</td>
                  <td className="num">{fmtUSD(i.total_gross)}</td>
                  <td className="num font-bold">{fmtUSD(i.total_share)}</td>
                  <td className="num">{fmtUSD(i.paid_amount)}</td>
                  <td>
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLES[i.status] || STATUS_STYLES.draft}`}>{i.status}</span>
                  </td>
                  <td>
                    <div className="flex gap-1 justify-end">
                      <a
                        href={`${API}/partner-invoices/${i.id}/print`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`print-partner-invoice-${i.id}`}
                        className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white transition-colors"
                        title="Print / PDF"
                      >
                        <Printer className="w-3 h-3" />
                      </a>
                      {emailConfig.configured && (
                        <button
                          data-testid={`email-partner-invoice-${i.id}`}
                          onClick={() => setEmailingId(i.id)}
                          className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white transition-colors"
                          title="Email invoice"
                        >
                          <Mail className="w-3 h-3" />
                        </button>
                      )}
                      {i.status !== "paid" && (
                        <button
                          data-testid={`pay-partner-invoice-${i.id}`}
                          onClick={() => setPayingId(i.id)}
                          className="p-1.5 border border-[#1A4135] text-[#1A4135] hover:bg-[#1A4135] hover:text-white transition-colors"
                          title="Record payment"
                        >
                          <DollarSign className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        data-testid={`delete-partner-invoice-${i.id}`}
                        onClick={() => onDelete(i.id)}
                        className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white transition-colors"
                        title="Delete invoice"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {genOpen && (
        <GenerateInvoiceModal
          partners={partners}
          preselectPartnerId={partnerParam}
          onClose={() => { setGenOpen(false); if (partnerParam) setParams({}); }}
          onCreated={() => { setGenOpen(false); if (partnerParam) setParams({}); load(); }}
        />
      )}

      {payingId && (
        <PayInvoiceModal
          invoice={invoices.find((i) => i.id === payingId)}
          onClose={() => setPayingId(null)}
          onPaid={() => { setPayingId(null); load(); }}
        />
      )}

      {emailingId && (
        <EmailInvoiceModal
          invoice={invoices.find((i) => i.id === emailingId)}
          onClose={() => setEmailingId(null)}
          onSent={() => { setEmailingId(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------- Generate Invoice Modal ----------
function GenerateInvoiceModal({ partners, preselectPartnerId, onClose, onCreated }) {
  const [partnerId, setPartnerId] = useState(preselectPartnerId || partners[0]?.id || "");
  const [partnerFields, setPartnerFields] = useState([]);
  const [selectedFieldIds, setSelectedFieldIds] = useState([]);
  const [periodStart, setPeriodStart] = useState(() => {
    const y = new Date().getFullYear();
    return `${y}-01-01`;
  });
  const [periodEnd, setPeriodEnd] = useState(todayISO());
  const [issueDate, setIssueDate] = useState(todayISO());
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [excluded, setExcluded] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Per-line adjustments keyed by expense_id: { [id]: [{label, amount_per_acre, amount_flat, direction}] }
  const [adjustmentsByLine, setAdjustmentsByLine] = useState({});
  const [expandedAdj, setExpandedAdj] = useState(new Set()); // which line rows have the adj editor expanded

  // Load partner's fields whenever partner changes
  useEffect(() => {
    if (!partnerId) { setPartnerFields([]); setSelectedFieldIds([]); return; }
    (async () => {
      const { data } = await api.get(`/partners/${partnerId}/summary`);
      setPartnerFields(data.fields || []);
      setSelectedFieldIds((data.fields || []).map((f) => f.id));
    })();
  }, [partnerId]);

  const runPreview = async () => {
    if (!partnerId) { toast.error("Pick a partner"); return; }
    if (!selectedFieldIds.length) { toast.error("Pick at least one field"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/partner-invoices/preview", {
        partner_id: partnerId,
        field_ids: selectedFieldIds,
        period_start: periodStart,
        period_end: periodEnd,
      });
      setPreview(data);
      setExcluded(new Set()); // reset exclusions
    } catch (e) {
      toast.error("Could not build preview");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpense = (expenseId) => {
    const next = new Set(excluded);
    next.has(expenseId) ? next.delete(expenseId) : next.add(expenseId);
    setExcluded(next);
  };

  // ---- Adjustments helpers ----
  const toggleAdjPanel = (expenseId) => {
    const next = new Set(expandedAdj);
    next.has(expenseId) ? next.delete(expenseId) : next.add(expenseId);
    setExpandedAdj(next);
  };
  const addAdjustment = (expenseId) => {
    setAdjustmentsByLine((prev) => ({
      ...prev,
      [expenseId]: [...(prev[expenseId] || []), { label: "Application", amount_per_acre: "", amount_flat: "", direction: "credit" }],
    }));
    // Keep the panel expanded when adding rows
    setExpandedAdj((prev) => new Set(prev).add(expenseId));
  };
  const updateAdjustment = (expenseId, idx, patch) => {
    setAdjustmentsByLine((prev) => {
      const arr = [...(prev[expenseId] || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...prev, [expenseId]: arr };
    });
  };
  const removeAdjustment = (expenseId, idx) => {
    setAdjustmentsByLine((prev) => {
      const arr = [...(prev[expenseId] || [])].filter((_, i) => i !== idx);
      const next = { ...prev, [expenseId]: arr };
      if (arr.length === 0) delete next[expenseId];
      return next;
    });
  };

  // Signed adjustment total for a single line (matches backend math)
  const computeLineAdjustment = (line) => {
    const adjs = adjustmentsByLine[line.expense_id] || [];
    let signed = 0;
    for (const a of adjs) {
      const flat = parseFloat(a.amount_flat) || 0;
      const perAcre = parseFloat(a.amount_per_acre) || 0;
      const mag = flat + perAcre * (line.acres || 0);
      signed += a.direction === "credit" ? -mag : mag;
    }
    return signed;
  };
  const lineBilled = (line) => {
    const base = (typeof line.base_share === "number" ? line.base_share : line.share_amount) || 0;
    return Math.round((base + computeLineAdjustment(line)) * 100) / 100;
  };

  const includedLines = preview ? preview.lines.filter((l) => !excluded.has(l.expense_id)) : [];
  const totalShare = includedLines.reduce((s, l) => s + lineBilled(l), 0);
  const totalGross = includedLines.reduce((s, l) => s + (l.gross_amount || 0), 0);
  const totalAdjust = includedLines.reduce((s, l) => s + computeLineAdjustment(l), 0);

  const onSave = async () => {
    if (!includedLines.length) { toast.error("Include at least one line"); return; }
    setSaving(true);
    try {
      // Serialize adjustments — strip empty rows and coerce numbers
      const cleanAdj = {};
      for (const id of Object.keys(adjustmentsByLine)) {
        if (!includedLines.find((l) => l.expense_id === id)) continue;
        const rows = (adjustmentsByLine[id] || []).filter((a) => (a.label || "").trim() && (parseFloat(a.amount_per_acre) || parseFloat(a.amount_flat)));
        if (rows.length) {
          cleanAdj[id] = rows.map((a) => ({
            label: a.label.trim(),
            amount_per_acre: parseFloat(a.amount_per_acre) || 0,
            amount_flat: parseFloat(a.amount_flat) || 0,
            direction: a.direction || "credit",
          }));
        }
      }
      await api.post("/partner-invoices", {
        partner_id: partnerId,
        field_ids: selectedFieldIds,
        period_start: periodStart,
        period_end: periodEnd,
        issue_date: issueDate,
        include_expense_ids: includedLines.map((l) => l.expense_id),
        line_adjustments: cleanAdj,
        notes,
      });
      toast.success("Partner invoice created");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save invoice");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Generate Partner Invoice" onClose={onClose}>
      <div className="space-y-4" data-testid="generate-partner-invoice-form">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Partner" required>
            <select data-testid="gen-partner-select" value={partnerId} onChange={(e) => setPartnerId(e.target.value)} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
              <option value="">Select…</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Issue Date" required>
            <input data-testid="gen-issue-date" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Period Start" required>
            <input data-testid="gen-period-start" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
          </Field>
          <Field label="Period End" required>
            <input data-testid="gen-period-end" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
          </Field>
        </div>

        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-1.5">Fields to bill</div>
          {partnerFields.length === 0 ? (
            <div className="text-[11px] text-[#BC4727] bg-[#FAF7EC] border border-[#E5E2D6] p-2">
              This partner isn&apos;t attached to any fields yet. Go to Fields and assign them with a split %.
            </div>
          ) : (
            <div className="border border-[#1F1E1B] max-h-40 overflow-y-auto" data-testid="gen-fields-list">
              {partnerFields.map((f) => (
                <label key={f.id} className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E2D6] last:border-0 hover:bg-[#F5F4F0] cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`gen-field-${f.id}`}
                    checked={selectedFieldIds.includes(f.id)}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedFieldIds([...selectedFieldIds, f.id]);
                      else setSelectedFieldIds(selectedFieldIds.filter((x) => x !== f.id));
                    }}
                  />
                  <span className="text-sm font-semibold">{f.name}</span>
                  <span className="text-xs text-[#66645E]">{fmtNum(f.acreage, 1)} ac · {f.crop_type}</span>
                  <span className="ml-auto text-xs stamp stamp-green">{fmtNum(f.partner_split_pct, 1)}%</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          data-testid="gen-preview-btn"
          onClick={runPreview}
          disabled={loading || !partnerId || !selectedFieldIds.length}
          className="w-full border-2 border-[#1F1E1B] py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white transition-colors disabled:opacity-40"
        >
          {loading ? "Building preview…" : "Preview Line Items →"}
        </button>

        {preview && (
          <div className="border-2 border-[#1F1E1B] bg-[#FAF7EC] p-3 space-y-3" data-testid="gen-preview-panel">
            {/* Per-Crop Breakdown */}
            {(preview.by_crop || []).length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-1.5">Per-Crop Breakdown</div>
                <div className="space-y-2">
                  {preview.by_crop.map((cr) => (
                    <div key={cr.crop} className="border-2 border-[#1F1E1B] bg-white" data-testid={`gen-crop-${cr.crop}`}>
                      <div className="bg-[#1F1E1B] text-white px-3 py-1.5 flex items-center justify-between text-xs">
                        <div>
                          <span className="text-[9px] uppercase tracking-[0.18em] text-[#B4B0A3]">Crop</span>
                          <span className="ml-2 font-bold text-sm">{cr.crop}</span>
                          <span className="ml-2 text-[#B4B0A3]">{fmtNum(cr.acres, 1)} ac</span>
                        </div>
                        <div className="tabular font-bold">
                          {fmtUSD(cr.share)} <span className="text-[#B4B0A3] text-[10px]">({fmtUSD(cr.per_acre_share)}/ac)</span>
                        </div>
                      </div>
                      <table className="w-full text-xs">
                        <tbody>
                          {cr.categories.map((c) => (
                            <tr key={c.category} className="border-t border-[#E5E2D6]">
                              <td className="px-3 py-1">{c.label}</td>
                              <td className="px-3 py-1 text-right tabular">{fmtUSD(c.share)}</td>
                              <td className="px-3 py-1 text-right tabular text-[#66645E]">{fmtUSD(c.per_acre_share)}/ac</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Per-Category Summary (overall) */}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-1.5">Overall Per-Category Summary</div>
              {Object.keys(preview.by_category || {}).length === 0 ? (
                <div className="text-xs text-[#66645E]">No matching expenses in this window.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[#66645E]">
                      <th className="text-left py-1">Category</th>
                      <th className="text-right py-1">Gross</th>
                      <th className="text-right py-1">Partner Share</th>
                      <th className="text-right py-1">Per Acre (avg)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(preview.by_category).map(([k, v]) => (
                      <tr key={k} className="border-t border-[#E5E2D6]">
                        <td className="py-1">{v.label}</td>
                        <td className="text-right tabular">{fmtUSD(v.gross)}</td>
                        <td className="text-right tabular font-bold">{fmtUSD(v.share)}</td>
                        <td className="text-right tabular text-[#66645E]">{fmtUSD(v.per_acre_share)}/ac</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Line detail with toggles + per-line adjustments */}
            {preview.lines.length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-1.5">
                  Line items <span className="text-[#66645E]">(uncheck to exclude · click &quot;+ adj&quot; to add per-line credit/charge)</span>
                </div>
                <div className="border border-[#1F1E1B] max-h-72 overflow-y-auto bg-white">
                  {preview.lines.map((l) => {
                    const on = !excluded.has(l.expense_id);
                    const adjs = adjustmentsByLine[l.expense_id] || [];
                    const expanded = expandedAdj.has(l.expense_id);
                    const signed = computeLineAdjustment(l);
                    const billed = lineBilled(l);
                    const baseShare = l.base_share ?? l.share_amount;
                    return (
                      <div key={l.expense_id} className={`border-b border-[#E5E2D6] last:border-0 ${on ? "" : "opacity-40"}`} data-testid={`gen-line-row-${l.expense_id}`}>
                        <div className={`flex items-center gap-2 px-2 py-1.5 text-xs ${on ? "" : "line-through"}`}>
                          <input type="checkbox" data-testid={`gen-line-${l.expense_id}`} checked={on} onChange={() => toggleExpense(l.expense_id)} />
                          <span className="tabular w-20">{l.date}</span>
                          <span className="stamp text-[9px]">{l.category_label}</span>
                          {l.crop && <span className="stamp text-[9px]" style={{ background: "#5C4A2B", color: "white", borderColor: "#5C4A2B" }}>{l.crop} · {fmtNum(l.acres, 1)}ac</span>}
                          <span className="flex-1 truncate">{l.field_name} · {l.description || l.vendor || ""}</span>
                          <span className="tabular text-[#66645E]">{fmtUSD(l.gross_amount)} × {l.split_pct}%</span>
                          <span className={`tabular font-bold w-20 text-right ${signed !== 0 ? "text-[#66645E] line-through-none no-underline" : ""}`} style={{ textDecoration: signed !== 0 ? "line-through" : "none" }}>{fmtUSD(baseShare)}</span>
                          {signed !== 0 && (
                            <span className={`tabular font-bold w-20 text-right ${signed < 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`}>{fmtUSD(billed)}</span>
                          )}
                          <button
                            type="button"
                            data-testid={`gen-line-adj-toggle-${l.expense_id}`}
                            onClick={(e) => { e.preventDefault(); toggleAdjPanel(l.expense_id); }}
                            className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${adjs.length ? "border-[#1A4135] text-[#1A4135]" : "border-[#66645E] text-[#66645E]"} hover:bg-[#1F1E1B] hover:text-white transition-colors`}
                          >
                            {adjs.length ? `${adjs.length} adj ${expanded ? "▲" : "▼"}` : "+ adj"}
                          </button>
                        </div>
                        {expanded && (
                          <div className="bg-[#FAF7EC] border-t border-[#E5E2D6] px-3 py-2 space-y-1.5" data-testid={`gen-line-adj-panel-${l.expense_id}`}>
                            {adjs.length === 0 && (
                              <div className="text-[10px] text-[#66645E] italic">
                                Add a credit (you eat the cost — reduces partner&apos;s bill) or a charge (partner pays extra).
                              </div>
                            )}
                            {adjs.map((a, idx) => {
                              const flat = parseFloat(a.amount_flat) || 0;
                              const perAcre = parseFloat(a.amount_per_acre) || 0;
                              const mag = flat + perAcre * (l.acres || 0);
                              const sign = a.direction === "credit" ? -1 : 1;
                              const preview = mag * sign;
                              return (
                                <div key={idx} className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto_auto_auto] gap-1.5 items-center text-[11px]">
                                  <input
                                    data-testid={`gen-adj-label-${l.expense_id}-${idx}`}
                                    value={a.label}
                                    onChange={(e) => updateAdjustment(l.expense_id, idx, { label: e.target.value })}
                                    placeholder="Label (e.g. Application)"
                                    className="bg-white border border-[#1F1E1B] px-2 py-1"
                                  />
                                  <div className="flex items-center gap-1">
                                    <span className="text-[10px] text-[#66645E]">$/ac</span>
                                    <input
                                      data-testid={`gen-adj-perac-${l.expense_id}-${idx}`}
                                      type="number" step="0.01"
                                      value={a.amount_per_acre}
                                      onChange={(e) => updateAdjustment(l.expense_id, idx, { amount_per_acre: e.target.value })}
                                      className="w-16 bg-white border border-[#1F1E1B] px-1 py-1 tabular"
                                      placeholder="8"
                                    />
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-[10px] text-[#66645E]">$flat</span>
                                    <input
                                      data-testid={`gen-adj-flat-${l.expense_id}-${idx}`}
                                      type="number" step="0.01"
                                      value={a.amount_flat}
                                      onChange={(e) => updateAdjustment(l.expense_id, idx, { amount_flat: e.target.value })}
                                      className="w-16 bg-white border border-[#1F1E1B] px-1 py-1 tabular"
                                      placeholder="0"
                                    />
                                  </div>
                                  <select
                                    data-testid={`gen-adj-dir-${l.expense_id}-${idx}`}
                                    value={a.direction}
                                    onChange={(e) => updateAdjustment(l.expense_id, idx, { direction: e.target.value })}
                                    className="bg-white border border-[#1F1E1B] px-1 py-1 text-[10px] font-bold uppercase"
                                  >
                                    <option value="credit">Credit ↓</option>
                                    <option value="charge">Charge ↑</option>
                                  </select>
                                  <span className={`tabular w-20 text-right font-bold ${preview < 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`}>{preview < 0 ? "−" : "+"}{fmtUSD(Math.abs(preview))}</span>
                                  <button type="button" data-testid={`gen-adj-del-${l.expense_id}-${idx}`} onClick={() => removeAdjustment(l.expense_id, idx)} className="px-1.5 py-0.5 text-[10px] text-[#BC4727] border border-[#BC4727] hover:bg-[#BC4727] hover:text-white">×</button>
                                </div>
                              );
                            })}
                            <button
                              type="button"
                              data-testid={`gen-adj-add-${l.expense_id}`}
                              onClick={() => addAdjustment(l.expense_id)}
                              className="w-full py-1 border border-dashed border-[#1F1E1B] text-[10px] font-bold uppercase tracking-wider text-[#66645E] hover:bg-[#1F1E1B] hover:text-white transition-colors"
                            >
                              + Add credit / charge
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Bottom running totals */}
            <div className="bg-[#1F1E1B] text-white px-3 py-2 flex items-center justify-between text-sm" data-testid="gen-totals">
              <div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#9E9B90]">Included</span>
                <span className="ml-2 font-bold">{includedLines.length} of {preview.lines.length} lines</span>
                <span className="ml-4 text-[10px] uppercase tracking-[0.18em] text-[#9E9B90]">Gross</span>
                <span className="ml-2 tabular">{fmtUSD(totalGross)}</span>
                {totalAdjust !== 0 && (
                  <>
                    <span className="ml-4 text-[10px] uppercase tracking-[0.18em] text-[#9E9B90]">Adjustments</span>
                    <span className={`ml-2 tabular font-bold ${totalAdjust < 0 ? "text-[#8DBFA8]" : "text-[#FFA88F]"}`}>{totalAdjust < 0 ? "−" : "+"}{fmtUSD(Math.abs(totalAdjust))}</span>
                  </>
                )}
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#9E9B90]">Amount Due</div>
                <div className="font-display font-black text-2xl tabular">{fmtUSD(totalShare)}</div>
              </div>
            </div>
          </div>
        )}

        <Field label="Notes (optional — printed on the invoice)">
          <textarea data-testid="gen-notes-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
          <button
            type="button"
            data-testid="gen-save-btn"
            onClick={onSave}
            disabled={saving || !preview || includedLines.length === 0}
            className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128] disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save Invoice"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------- Payment Modal ----------
function PayInvoiceModal({ invoice, onClose, onPaid }) {
  const [amount, setAmount] = useState(invoice?.total_share || 0);
  const [date, setDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/partner-invoices/${invoice.id}/mark-paid`, {
        paid_amount: parseFloat(amount) || 0,
        paid_date: date,
      });
      toast.success("Payment recorded — Schedule F updated");
      onPaid();
    } catch {
      toast.error("Could not record payment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Record Payment · ${invoice?.invoice_number || ""}`} onClose={onClose}>
      <div className="space-y-4" data-testid="pay-partner-invoice-form">
        <div className="bg-[#FAF7EC] border border-[#E5E2D6] p-3 text-sm">
          <div className="flex justify-between mb-1">
            <span className="text-[#66645E]">Partner</span>
            <span className="font-bold">{invoice?.partner_name}</span>
          </div>
          <div className="flex justify-between mb-1">
            <span className="text-[#66645E]">Total Due</span>
            <span className="font-bold tabular">{fmtUSD(invoice?.total_share)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#66645E]">Previously Paid</span>
            <span className="tabular">{fmtUSD(invoice?.paid_amount)}</span>
          </div>
        </div>
        <Field label="Amount Received" required>
          <input data-testid="pay-amount-input" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
        </Field>
        <Field label="Payment Date" required>
          <input data-testid="pay-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
        </Field>
        <div className="text-[11px] text-[#66645E] bg-[#FAF7EC] border border-[#E5E2D6] p-2">
          Recording payment reduces your Schedule F expense total by the reimbursed amount — so the tax report shows what you truly bore.
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
          <button type="button" data-testid="pay-submit-btn" onClick={submit} disabled={saving} className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128] disabled:opacity-40 flex items-center gap-2">
            <Check className="w-4 h-4" />{saving ? "Saving…" : "Record Payment"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------- Email Modal ----------
function EmailInvoiceModal({ invoice, onClose, onSent }) {
  const [to, setTo] = useState(invoice?.partner_email || "");
  const [subject, setSubject] = useState(`Cost-Share Invoice ${invoice?.invoice_number || ""} — ${fmtUSD(invoice?.total_share || 0)} due`);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!to) { toast.error("Enter a recipient email"); return; }
    setSending(true);
    try {
      await api.post(`/partner-invoices/${invoice.id}/email`, {
        recipient_email: to,
        subject,
        message,
      });
      toast.success("Invoice emailed");
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal title={`Email ${invoice?.invoice_number || ""}`} onClose={onClose}>
      <div className="space-y-4" data-testid="email-partner-invoice-form">
        <Field label="To" required>
          <input data-testid="email-to-input" type="email" value={to} onChange={(e) => setTo(e.target.value)} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
        </Field>
        <Field label="Subject">
          <input data-testid="email-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
        </Field>
        <Field label="Message (optional, appears above the invoice)">
          <textarea data-testid="email-message-input" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" placeholder="Hey Uncle — here's the invoice for our shared field. Let me know if you have questions." />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
          <button type="button" data-testid="email-send-btn" onClick={send} disabled={sending} className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128] disabled:opacity-40 flex items-center gap-2">
            <Send className="w-4 h-4" />{sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
